import { invariantResponse } from '@epic-web/invariant'
import { json, type LoaderFunctionArgs } from '@remix-run/node'
import { Link, useLoaderData, type MetaFunction } from '@remix-run/react'
import { brandName } from '#app/components/branding.js'
import { GeneralErrorBoundary } from '#app/components/error-boundary.tsx'
import { prisma } from '#app/utils/db.server.ts'
import { getUserImgSrc } from '#app/utils/misc.tsx'

export async function loader({ params }: LoaderFunctionArgs) {
	const owner = await prisma.user.findFirst({
		select: {
			id: true,
			name: true,
			username: true,
			image: { select: { id: true } },
			guilds: {
				select: {
					id: true,
					role: true,
					details: true,
					yearStart: true,
					yearEnd: true,
					guild: {
						select: {
							id: true,
							name: true,
							abbreviation: true,
							description: true,
							shard: {
								select: {
									name: true,
									slug: true,
									server: {
										select: { name: true, slug: true },
									},
								},
							},
							_count: { select: { users: true } },
						},
					},
				},
				orderBy: { createdAt: 'desc' },
			},
		},
		where: { username: params.username },
	})

	invariantResponse(owner, 'Owner not found', { status: 404 })

	return json({ owner })
}

export default function GuildsRoute() {
	const { owner } = useLoaderData<typeof loader>()
	const ownerDisplayName = owner.name ?? owner.username

	return (
		<div className="container mx-auto mb-24 px-4 pt-8 md:mb-48">
			{/* Header */}
			<div className="flex items-center gap-3">
				<Link to={`/users/${owner.username}`}>
					<img
						src={getUserImgSrc(owner.image?.id)}
						alt={ownerDisplayName}
						className="h-10 w-10 rounded-full object-cover md:h-12 md:w-12"
					/>
				</Link>
				<div>
					<Link
						to={`/users/${owner.username}`}
						className="text-sm text-muted-foreground hover:underline"
					>
						&larr; {ownerDisplayName}
					</Link>
					<h1 className="text-xl font-bold md:text-2xl">
						Guild History ({owner.guilds.length})
					</h1>
				</div>
			</div>

			{/* Guilds list */}
			<div className="mt-6 space-y-3">
				{owner.guilds.length === 0 && (
					<p className="text-sm text-muted-foreground">
						No guild history listed.
					</p>
				)}
				{owner.guilds.map((membership) => {
					const server = membership.guild.shard?.server[0]
					const shard = membership.guild.shard
					const guildLink =
						server && shard
							? `/servers/${server.slug}/shards/${shard.slug}/guilds/${membership.guild.id}`
							: null

					return (
						<div
							key={membership.id}
							className="rounded-xl border bg-muted/30 p-4"
						>
							<div className="flex items-start justify-between gap-2">
								<div className="min-w-0">
									<div className="flex items-center gap-2 flex-wrap">
										{guildLink ? (
											<Link
												to={guildLink}
												className="font-semibold hover:underline"
											>
												{membership.guild.name}
											</Link>
										) : (
											<span className="font-semibold">
												{membership.guild.name}
											</span>
										)}
										{membership.guild.abbreviation && (
											<span className="text-xs text-muted-foreground">
												[
												{
													membership.guild
														.abbreviation
												}
												]
											</span>
										)}
										{membership.role && (
											<span className="rounded bg-muted px-2 py-0.5 text-xs text-muted-foreground">
												{membership.role}
											</span>
										)}
									</div>
									<div className="mt-1 flex items-center gap-2 text-xs text-muted-foreground">
										{server && shard && (
											<span>
												{server.name} · {shard.name}
											</span>
										)}
										{(membership.yearStart ||
											membership.yearEnd) && (
											<span>
												{membership.yearStart ?? '?'} –{' '}
												{membership.yearEnd ??
													'present'}
											</span>
										)}
									</div>
								</div>
								<span className="shrink-0 rounded-full bg-muted px-2 py-0.5 text-xs text-muted-foreground">
									{membership.guild._count.users}{' '}
									{membership.guild._count.users === 1
										? 'member'
										: 'members'}
								</span>
							</div>
							{membership.guild.description && (
								<p className="mt-2 text-sm text-muted-foreground line-clamp-2">
									{membership.guild.description}
								</p>
							)}
							{membership.details && (
								<p className="mt-2 text-sm italic text-muted-foreground">
									"{membership.details}"
								</p>
							)}
						</div>
					)
				})}
			</div>
		</div>
	)
}

export const meta: MetaFunction<typeof loader> = ({ data, params }) => {
	const displayName = data?.owner.name ?? params.username
	const count = data?.owner.guilds.length ?? 0
	return [
		{ title: `${displayName}'s Guilds | ${brandName}` },
		{
			name: 'description',
			content: `${displayName}'s ${count} guild${count !== 1 ? 's' : ''} on ${brandName}`,
		},
	]
}

export function ErrorBoundary() {
	return (
		<GeneralErrorBoundary
			statusHandlers={{
				404: ({ params }) => (
					<p>
						No user with the username "{params.username}" exists
					</p>
				),
			}}
		/>
	)
}
